/**
 * KOAI 사용자 매뉴얼 (Documentation)
 * Prompting / Report 산출물 기준으로 가입·로그인·핵심 기능을 안내
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  Button,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  MenuBook as DocsIcon,
  RocketLaunch as TryIcon,
  VpnKey as KeyIcon,
  Login as LoginIcon,
  Dashboard as DashboardIcon,
  Chat as ChatIcon,
  Person as MyPageIcon,
  Announcement as NoticeIcon,
  Extension as ExtensionIcon,
  Payment as PlanIcon,
  AdminPanelSettings as AdminIcon,
  HelpOutline as FaqIcon,
} from '@mui/icons-material';

const SECTIONS = [
  { id: 'overview', label: '시작하기', icon: DocsIcon },
  { id: 'try', label: '무가입 체험', icon: TryIcon },
  { id: 'register', label: '인증키 만들기', icon: KeyIcon },
  { id: 'login', label: '로그인', icon: LoginIcon },
  { id: 'dashboard', label: '대시보드', icon: DashboardIcon },
  { id: 'chat', label: 'AI 채팅', icon: ChatIcon },
  { id: 'mypage', label: '마이페이지', icon: MyPageIcon },
  { id: 'plans', label: '요금제·구독', icon: PlanIcon },
  { id: 'notices', label: '공지사항', icon: NoticeIcon },
  { id: 'extensions', label: '확장 기능', icon: ExtensionIcon },
  { id: 'admin', label: '관리자 기능', icon: AdminIcon },
  { id: 'faq', label: 'FAQ', icon: FaqIcon },
];

function StepList({ steps }) {
  return (
    <Box component="ol" sx={{ pl: 2.5, m: 0, '& li': { mb: 1.25 } }}>
      {steps.map((step, i) => (
        <Typography component="li" key={i} variant="body1" color="text.primary">
          {step}
        </Typography>
      ))}
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
      {children}
    </Typography>
  );
}

function SubTitle({ children }) {
  return (
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
      {children}
    </Typography>
  );
}

function Body({ children }) {
  return (
    <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.7 }}>
      {children}
    </Typography>
  );
}

export default function DocsPage() {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState('overview');

  const handleChange = (panel) => (_event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const jumpTo = (id) => {
    setExpanded(id);
    requestAnimationFrame(() => {
      document.getElementById(`docs-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <DocsIcon color="primary" />
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Documentation
          </Typography>
        </Stack>
        <Body>
          KOAI 사용자 매뉴얼입니다. 인증키(인증코드) 발급부터 로그인, AI 채팅, 요금제, 확장 기능까지
          실제 화면 기준으로 안내합니다.
        </Body>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '240px 1fr' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        {/* 목차 */}
        <Paper
          variant="outlined"
          sx={{
            p: 1,
            position: { md: 'sticky' },
            top: { md: 16 },
            maxHeight: { md: 'calc(100vh - 120px)' },
            overflow: 'auto',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, py: 1, display: 'block' }}>
            목차
          </Typography>
          <List dense disablePadding>
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              return (
                <ListItem key={s.id} disablePadding>
                  <ListItemButton selected={expanded === s.id} onClick={() => jumpTo(s.id)}>
                    <Icon fontSize="small" sx={{ mr: 1.25, opacity: 0.8 }} />
                    <ListItemText primary={s.label} primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Paper>

        {/* 본문 */}
        <Box>
          {/* 1. 시작하기 */}
          <Accordion
            id="docs-overview"
            expanded={expanded === 'overview'}
            onChange={handleChange('overview')}
            disableGutters
            sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <DocsIcon fontSize="small" color="primary" />
                <Typography fontWeight={600}>1. 시작하기 — KOAI란?</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Body>
                KOAI(Knowledge + Organization + AI)는 문서를 올리고 질문하면, 문서에 근거한 답변과
                출처를 함께 제공하는 HTP-RAG 기반 AI 지식 검색 서비스입니다.
              </Body>
              <SubTitle>핵심 원칙</SubTitle>
              <StepList
                steps={[
                  '근거 기반 답변: 올린 문서에 있는 정보만 사용합니다.',
                  '모르면 인정: 문서에서 찾을 수 없으면 추측하지 않습니다.',
                  '출처 명시: 답변에 사용된 문서·발췌를 [출처]로 표시합니다.',
                  '임시 분석: SaaS 채팅에서는 문서를 세션 동안만 분석하며, 원본은 영구 저장하지 않습니다.',
                ]}
              />
              <SubTitle>추천 시작 경로</SubTitle>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                <Button variant="contained" onClick={() => navigate('/chat')}>
                  AI 채팅 열기
                </Button>
                <Button variant="outlined" onClick={() => jumpTo('register')}>
                  인증키 만들기부터
                </Button>
                <Button variant="text" onClick={() => jumpTo('try')}>
                  무가입 체험 안내
                </Button>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* 2. 무가입 체험 */}
          <Accordion
            id="docs-try"
            expanded={expanded === 'try'}
            onChange={handleChange('try')}
            disableGutters
            sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <TryIcon fontSize="small" color="primary" />
                <Typography fontWeight={600}>2. 무가입 체험 (/try)</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Body>
                회원가입 없이 60초 안에 「문서 올리기 → 질문 → 출처와 함께 답」을 체험할 수 있습니다.
                랜딩 페이지의 「설치 없이 지금 써보기」 또는 「샘플로 30초 체험」으로 진입합니다.
              </Body>
              <StepList
                steps={[
                  '문서 올리기: PDF·Office 등을 업로드하거나 「샘플로 30초 체험」을 선택합니다.',
                  '질문하기: 직접 입력하거나 데모 질문 칩을 클릭합니다.',
                  '출처와 함께 답: 답변과 관련 출처(파일명·관련도·발췌)를 확인합니다.',
                  'Office 저장: 답변 아래 Word / PowerPoint / Excel 버튼으로 내려받습니다.',
                  '계속 쓰기: 「계정 만들기」로 가입하면 로그인 후에도 동일하게 사용할 수 있습니다.',
                ]}
              />
              <Alert severity="info" sx={{ mt: 2 }}>
                체험 세션의 문서는 임시(Ephemeral)로만 분석됩니다. 대화 저장·일일 한도·구독 관리는
                로그인 계정에서 이용합니다.
              </Alert>
            </AccordionDetails>
          </Accordion>

          {/* 3. 인증키 만들기 */}
          <Accordion
            id="docs-register"
            expanded={expanded === 'register'}
            onChange={handleChange('register')}
            disableGutters
            sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <KeyIcon fontSize="small" color="primary" />
                <Typography fontWeight={600}>3. 인증키(인증코드) 만들기</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Body>
                KOAI SaaS는 비밀번호 대신 <strong>128자 인증코드(인증키)</strong>로 로그인합니다.
                이메일로 계정을 만들고 인증코드를 받으면 됩니다.
              </Body>
              <SectionTitle>발급 절차</SectionTitle>
              <StepList
                steps={[
                  '랜딩 페이지(/)에서 「이메일로 계정 생성」을 클릭하거나 /#/register 로 이동합니다.',
                  '이메일 주소를 입력하고 「인증코드 받기」를 누릅니다.',
                  '화면에 표시된 128자 인증코드를 복사합니다. (이메일로도 발송됩니다)',
                  '인증코드를 안전한 곳에 보관하세요. 타인에게 공유하면 안 됩니다.',
                  '랜딩 페이지의 인증코드 입력란에 붙여넣고 「시작하기」로 로그인합니다.',
                ]}
              />
              <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                인증코드를 분실하면 「인증코드 찾기」(/#/forgot-password)에서 가입 이메일로 새 코드를
                받을 수 있습니다. 재발급 시 기존 코드는 무효화됩니다.
              </Alert>
              <SubTitle>로그인 후 확인·재발급</SubTitle>
              <Body>
                사이드바 「마이페이지」에서 현재 인증코드를 보거나 복사하고, 필요하면 재발급할 수
                있습니다. 재발급 후에는 새 코드로만 로그인됩니다.
              </Body>
              <Button variant="outlined" size="small" onClick={() => navigate('/mypage')}>
                마이페이지로 이동
              </Button>
            </AccordionDetails>
          </Accordion>

          {/* 4. 로그인 */}
          <Accordion
            id="docs-login"
            expanded={expanded === 'login'}
            onChange={handleChange('login')}
            disableGutters
            sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <LoginIcon fontSize="small" color="primary" />
                <Typography fontWeight={600}>4. 로그인하는 방법</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <SubTitle>일반 사용자 (인증코드)</SubTitle>
              <StepList
                steps={[
                  '랜딩 페이지(/)의 인증코드 입력란에 128자 코드를 붙여넣습니다.',
                  'Enter 또는 「시작하기」를 누르면 대시보드로 이동합니다.',
                  '코드 길이가 128자가 아니면 오류가 표시됩니다. 앞뒤 공백 없이 전체를 붙여넣으세요.',
                ]}
              />
              <SubTitle>인증코드를 잊었을 때</SubTitle>
              <StepList
                steps={[
                  '「인증코드를 잊으셨나요? 인증코드 찾기」를 클릭합니다.',
                  '가입한 이메일을 입력하고 새 인증코드를 받습니다.',
                  '받은 코드로 다시 로그인합니다.',
                ]}
              />
              <SubTitle>플랫폼 관리자</SubTitle>
              <Body>
                관리자 계정은 /#/login 에서 이메일·비밀번호로 로그인할 수 있습니다. 일반 사용자는
                인증코드 방식을 사용하세요.
              </Body>
              <SubTitle>로그아웃</SubTitle>
              <Body>
                우측 상단 프로필 아이콘 → 로그아웃. 로그아웃 후에는 랜딩 페이지로 이동합니다.
              </Body>
            </AccordionDetails>
          </Accordion>

          {/* 5. 대시보드 */}
          <Accordion
            id="docs-dashboard"
            expanded={expanded === 'dashboard'}
            onChange={handleChange('dashboard')}
            disableGutters
            sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <DashboardIcon fontSize="small" color="primary" />
                <Typography fontWeight={600}>5. 대시보드</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Body>
                로그인 후 첫 화면입니다. 바로 가기, 오늘 이용 현황, 최근 대화·공지, 간단한 사용
                가이드를 제공합니다.
              </Body>
              <StepList
                steps={[
                  '바로 가기: AI 채팅, 문서 분석(채팅으로 이동), 마이페이지, 공지사항으로 이동',
                  '오늘 이용 현황: 무료 요금제는 일일 질문 한도(30회)와 남은 횟수를 표시',
                  '최근 대화: 이전 채팅 세션을 다시 엽니다',
                  '공지사항: 중요·고정 공지를 확인합니다',
                  '이렇게 사용하세요: 문서 올리기 → 질문 → Office 저장 3단계 안내',
                ]}
              />
            </AccordionDetails>
          </Accordion>

          {/* 6. AI 채팅 */}
          <Accordion
            id="docs-chat"
            expanded={expanded === 'chat'}
            onChange={handleChange('chat')}
            disableGutters
            sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <ChatIcon fontSize="small" color="primary" />
                <Typography fontWeight={600}>6. AI 채팅 — 문서 분석·질의·내보내기</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Body>
                KOAI의 핵심 기능입니다. 문서를 첨부하고 질문하면 HTP-RAG가 관련 구간을 찾아 출처와
                함께 답합니다.
              </Body>
              <SectionTitle>기본 사용 흐름</SectionTitle>
              <StepList
                steps={[
                  '사이드바 「AI 채팅」을 엽니다. 「새 대화」로 세션을 시작할 수 있습니다.',
                  '입력창 옆 + 버튼으로 문서를 첨부합니다. 분석이 끝나면 질문할 수 있습니다.',
                  '문서 내용을 바탕으로 질문합니다. (예: “연차 신청 절차를 요약해 주세요”)',
                  '답변과 함께 출처(파일명·관련도·발췌)를 확인합니다.',
                  '답변 아래 Word · PowerPoint · Excel 버튼으로 결과를 저장합니다.',
                ]}
              />
              <SubTitle>지원 문서 형식 (대표)</SubTitle>
              <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 2 }}>
                {['PDF', 'Word', 'Excel', 'PowerPoint', 'TXT', 'HWP/HWPX', '이미지(PNG/JPEG/BMP/GIF)'].map(
                  (f) => (
                    <Chip key={f} label={f} size="small" variant="outlined" />
                  )
                )}
              </Stack>
              <Body>
                한글(HWP/HWPX 등)과 이미지는 내부적으로 PDF·텍스트 변환 후 분석됩니다. 브라우저가
                MIME을 알 수 없는 경우에도 확장자로 판별합니다.
              </Body>
              <SubTitle>출처와 환각 억제</SubTitle>
              <Body>
                답변은 올린 문서에 근거합니다. 관련 내용이 없으면 “모른다”고 응답할 수 있으며, 이는
                잘못된 추측을 줄이기 위한 HTP-RAG의 동작입니다. 중요한 의사결정 전에는 출처 발췌를
                반드시 확인하세요.
              </Body>
              <SubTitle>일일 질문 한도</SubTitle>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>요금제</TableCell>
                      <TableCell>일일 질문</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>무료 (free)</TableCell>
                      <TableCell>하루 30회</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>스탠다드 (paid)</TableCell>
                      <TableCell>무제한</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>플랫폼 관리자</TableCell>
                      <TableCell>무제한</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <Alert severity="info">
                문서 컨텍스트는 계정별 세션에 유지됩니다. 브라우저를 닫아도 같은 계정으로 다시
                들어오면 이어서 질문할 수 있습니다. (세션이 만료되면 문서를 다시 올리면 됩니다)
              </Alert>
            </AccordionDetails>
          </Accordion>

          {/* 7. 마이페이지 */}
          <Accordion
            id="docs-mypage"
            expanded={expanded === 'mypage'}
            onChange={handleChange('mypage')}
            disableGutters
            sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <MyPageIcon fontSize="small" color="primary" />
                <Typography fontWeight={600}>7. 마이페이지 — 인증코드·요금제</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <StepList
                steps={[
                  '인증코드 정보: 이메일, 요금제(무료/스탠다드), 인증코드 상태를 확인합니다.',
                  '현재 인증코드: 마스킹된 코드를 「보기」로 확인하고 「복사」할 수 있습니다.',
                  '인증코드 재발급: 새 코드를 발급하면 기존 코드는 즉시 무효화됩니다.',
                  '요금제·구독: 무료는 업그레이드, 스탠다드는 「구독 관리 (해지·결제수단)」로 Polar 포털에 연결됩니다.',
                ]}
              />
              <SubTitle>인증코드 사용 방법 (요약)</SubTitle>
              <StepList
                steps={[
                  '마이페이지에서 인증코드를 복사합니다.',
                  '로그아웃 후 랜딩 페이지에서 「인증코드 로그인」에 붙여넣습니다.',
                  '「시작하기」로 다시 로그인합니다.',
                ]}
              />
            </AccordionDetails>
          </Accordion>

          {/* 8. 요금제 */}
          <Accordion
            id="docs-plans"
            expanded={expanded === 'plans'}
            onChange={handleChange('plans')}
            disableGutters
            sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <PlanIcon fontSize="small" color="primary" />
                <Typography fontWeight={600}>8. 요금제와 구독</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Body>
                계정에는 요금 상태(plan)와 운영 권한(role)이 분리되어 있습니다. 일반 사용자는
                plan만 신경 쓰면 됩니다.
              </Body>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>구분</TableCell>
                      <TableCell>plan / role</TableCell>
                      <TableCell>주요 차이</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>무료</TableCell>
                      <TableCell>free / user</TableCell>
                      <TableCell>일 30회 질문, 업그레이드 CTA</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>스탠다드</TableCell>
                      <TableCell>paid / user</TableCell>
                      <TableCell>질문 무제한, Polar 구독 관리</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>엔터프라이즈</TableCell>
                      <TableCell>(문의)</TableCell>
                      <TableCell>온프레미스·조직 도입 — 별도 상담</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>플랫폼 관리자</TableCell>
                      <TableCell>* / admin</TableCell>
                      <TableCell>사용자·플랫폼·설정·공지 작성</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <SubTitle>스탠다드로 업그레이드</SubTitle>
              <StepList
                steps={[
                  '마이페이지 또는 대시보드의 「스탠다드 업그레이드」를 클릭합니다.',
                  'Polar 결제 페이지에서 결제를 완료합니다.',
                  '완료 후 서비스로 돌아오면 plan이 paid(스탠다드)로 반영됩니다.',
                ]}
              />
              <SubTitle>구독 해지·결제수단 변경</SubTitle>
              <Body>
                스탠다드 사용자는 마이페이지 → 「구독 관리 (해지·결제수단)」 → Polar Customer Portal에서
                해지하거나 결제수단을 변경할 수 있습니다. 해지 반영은 웹훅으로 plan이 free로
                돌아갑니다.
              </Body>
            </AccordionDetails>
          </Accordion>

          {/* 9. 공지사항 */}
          <Accordion
            id="docs-notices"
            expanded={expanded === 'notices'}
            onChange={handleChange('notices')}
            disableGutters
            sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <NoticeIcon fontSize="small" color="primary" />
                <Typography fontWeight={600}>9. 공지사항</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <StepList
                steps={[
                  '사이드바 「공지사항」에서 서비스 안내·점검·업데이트를 확인합니다.',
                  '「중요」 배지·고정(핀)된 공지가 목록 상단에 우선 표시될 수 있습니다.',
                  '항목을 클릭하면 본문·작성자·날짜·조회수를 볼 수 있습니다.',
                  '공지 작성·수정·삭제는 플랫폼 관리자(role=admin)만 가능합니다.',
                ]}
              />
            </AccordionDetails>
          </Accordion>

          {/* 10. 확장 */}
          <Accordion
            id="docs-extensions"
            expanded={expanded === 'extensions'}
            onChange={handleChange('extensions')}
            disableGutters
            sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <ExtensionIcon fontSize="small" color="primary" />
                <Typography fontWeight={600}>10. 확장 기능 (PromptClinic · DeciCraft · AIDOS)</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Body>
                사이드바 「확장」에서 부가 도구를 사용할 수 있습니다. 코어 업무(문서 질의)를 익힌 뒤
                필요에 따라 활용하세요.
              </Body>
              <SubTitle>PromptClinic</SubTitle>
              <Body>
                프롬프트를 P-C-T-F 관점으로 분석하고 개선안을 제안합니다. 분석 결과를 저장하면
                사이드바 확장 메뉴 아래에 저장된 항목이 나타나며, 클릭해 다시 불러올 수 있습니다.
              </Body>
              <SubTitle>DeciCraft</SubTitle>
              <Body>
                의사결정 상황을 정리·분석하고 결과를 저장·재조회할 수 있습니다. PromptClinic과
                동일하게 사이드바에 저장 이력이 표시됩니다.
              </Body>
              <SubTitle>AIDOS (의사결정 데스크)</SubTitle>
              <StepList
                steps={[
                  '요청 내용을 작성하고 필요하면 관련 문서를 첨부합니다.',
                  'AI가 리스크·권고를 평가합니다.',
                  '승인 / 거절 후 실행 단계로 진행할 수 있습니다.',
                ]}
              />
              <Alert severity="info" sx={{ mt: 2 }}>
                데모·PoC의 공식 해피패스는 「문서 업로드 → 질문 → 출처 확인 → (선택) Office 저장」입니다.
                확장 기능은 도입 후 업셀·심화용으로 보시면 됩니다.
              </Alert>
            </AccordionDetails>
          </Accordion>

          {/* 11. 관리자 */}
          <Accordion
            id="docs-admin"
            expanded={expanded === 'admin'}
            onChange={handleChange('admin')}
            disableGutters
            sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <AdminIcon fontSize="small" color="primary" />
                <Typography fontWeight={600}>11. 관리자 기능 (admin)</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Body>
                role=admin 계정에만 「관리」 섹션(사용자 관리, 플랫폼 관리, 설정)과 공지 작성이
                보입니다. Polar 결제로 admin 권한이 부여되지는 않습니다.
              </Body>
              <StepList
                steps={[
                  '사용자 관리: 사용자 목록 조회, 정보·요금제·상태 편집, 구독 이력 확인',
                  '플랫폼 관리: 플랫폼 통계·모니터링',
                  '설정: 시스템·AI 모델 등 관리자 설정',
                  '공지사항: 중요/고정 공지 작성·수정·삭제',
                ]}
              />
            </AccordionDetails>
          </Accordion>

          {/* 12. FAQ */}
          <Accordion
            id="docs-faq"
            expanded={expanded === 'faq'}
            onChange={handleChange('faq')}
            disableGutters
            sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FaqIcon fontSize="small" color="primary" />
                <Typography fontWeight={600}>12. FAQ · 문제 해결</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <SubTitle>Q. 인증코드가 128자가 아니라고 나옵니다.</SubTitle>
              <Body>
                복사할 때 앞뒤 공백·줄바꿈이 들어갔는지 확인하세요. 전체를 한 번에 복사해
                붙여넣으세요.
              </Body>
              <SubTitle>Q. 문서를 올렸는데 질문이 안 됩니다.</SubTitle>
              <Body>
                분석(인덱싱)이 끝날 때까지 잠시 기다린 뒤 다시 질문하세요. 세션이 만료된 경우
                문서를 다시 첨부합니다.
              </Body>
              <SubTitle>Q. 무료 한도에 도달했습니다.</SubTitle>
              <Body>
                대시보드·마이페이지에서 스탠다드로 업그레이드하면 일일 한도가 해제됩니다. 다음 날
                0시 기준으로 무료 한도가 초기화됩니다.
              </Body>
              <SubTitle>Q. 답변에 출처가 없습니다.</SubTitle>
              <Body>
                문서와 관련 없는 질문이거나, 문서에서 근거를 찾지 못한 경우일 수 있습니다. 질문을
                구체화하거나 관련 문서를 추가로 올려 보세요.
              </Body>
              <SubTitle>Q. 구독을 해지했는데 여전히 스탠다드로 보입니다.</SubTitle>
              <Body>
                Polar 포털에서 해지 후 웹훅 반영까지 잠시 걸릴 수 있습니다. 페이지를 새로고침하거나
                다시 로그인해 보세요. 계속되면 관리자에게 문의하세요.
              </Body>
              <Divider sx={{ my: 2 }} />
              <Body>
                더 자세한 기술·배포 문서는 저장소의 Prompting / Report 폴더와 docs 디렉터리를
                참고하세요.
              </Body>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>
    </Box>
  );
}
